/**
 * =============================================================================
 * navigationPersistence.ts
 * =============================================================================
 * 
 * Utility condivise per preservare il contesto di navigazione tra reload,
 * sospensione della scheda e redirect temporanei verso /auth.
 * 
 * FUNZIONALITÀ PRINCIPALI:
 * - Salvataggio ultima route applicativa visitata
 * - Persistenza posizione di scroll per route
 * - Normalizzazione dei redirect post-auth
 * - Filtri per evitare restore su pagine pubbliche di autenticazione
 * - Logging diagnostico opzionale via navDebugLogger
 * =============================================================================
 */

import { logNavDebug } from "@/lib/navDebugLogger";

// ===== COSTANTI STORAGE =====

/** Ultima route protetta visitata, usata per tornare al punto corretto dopo /auth. */
export const LAST_INTENDED_ROUTE_STORAGE_KEY = "lni:last-intended-route";

/** Prefisso per memorizzare la posizione verticale della pagina per singola route. */
const SCROLL_POSITION_PREFIX = "lni:scroll:";

/** Route che deve ripristinare lo scroll al primo render utile dopo reload/restore. */
const RESTORE_SCROLL_ROUTE_STORAGE_KEY = "lni:restore-scroll-route";

/** Percorsi pubblici che non devono diventare target di restore automatico. */
const PUBLIC_PATH_PREFIXES = ["/auth", "/reset-password"];

// ===== HELPER GENERALI =====

/** Determina se un percorso appartiene all'area pubblica di autenticazione. */
export function isPublicAppPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

/** Costruisce un URL relativo completo preservando query string e hash. */
export function buildRelativeAppUrl(parts: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  return `${parts.pathname}${parts.search ?? ""}${parts.hash ?? ""}`;
}

/**
 * Valida una route candidata al redirect post-auth.
 * Accetta solo percorsi relativi interni e scarta le route pubbliche.
 */
export function normalizeAppRedirect(candidate: string | null | undefined): string | null {
  if (!candidate || typeof candidate !== "string") return null;
  if (!candidate.startsWith("/")) return null;
  if (candidate.startsWith("//")) return null;
  if (isPublicAppPath(candidate)) return null;
  return candidate;
}

/** Salva in sessione l'ultima route applicativa significativa. */
export function storeLastIntendedRoute(route: string): void {
  const normalized = normalizeAppRedirect(route);
  if (!normalized) return;

  try {
    window.sessionStorage.setItem(LAST_INTENDED_ROUTE_STORAGE_KEY, normalized);
    logNavDebug("route", "storeLastIntendedRoute", { route: normalized });
  } catch {
    /* sessionStorage non disponibile: ignoriamo silenziosamente */
  }
}

/** Legge l'ultima route applicativa salvata in sessione. */
export function readLastIntendedRoute(): string | null {
  try {
    return normalizeAppRedirect(
      window.sessionStorage.getItem(LAST_INTENDED_ROUTE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

/** Pulisce la route salvata dopo un restore riuscito. */
export function clearLastIntendedRoute(): void {
  try {
    window.sessionStorage.removeItem(LAST_INTENDED_ROUTE_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ===== PERSISTENZA SCROLL =====

/** Restituisce la chiave storage per lo scroll di una route specifica. */
function getScrollStorageKey(route: string): string {
  return `${SCROLL_POSITION_PREFIX}${route}`;
}

/** Salva la posizione verticale corrente per una route. */
export function storeScrollPosition(route: string, scrollY: number): void {
  const normalized = normalizeAppRedirect(route);
  if (!normalized || !Number.isFinite(scrollY)) return;

  try {
    window.sessionStorage.setItem(getScrollStorageKey(normalized), String(scrollY));
    logNavDebug("scroll", "storeScrollPosition", { route: normalized, scrollY });
  } catch {
    /* noop */
  }
}

/** Recupera la posizione verticale salvata per una route. */
export function readScrollPosition(route: string): number | null {
  const normalized = normalizeAppRedirect(route);
  if (!normalized) return null;

  try {
    const rawValue = window.sessionStorage.getItem(getScrollStorageKey(normalized));
    if (rawValue === null) return null;

    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Marca una route come candidata al restore dello scroll dopo reload/restore tab. */
export function markRouteForScrollRestore(route: string): void {
  const normalized = normalizeAppRedirect(route);
  if (!normalized) return;

  try {
    window.sessionStorage.setItem(RESTORE_SCROLL_ROUTE_STORAGE_KEY, normalized);
  } catch {
    /* noop */
  }
}

/** Indica se la route corrente deve ripristinare lo scroll salvato. */
export function shouldRestoreScrollForRoute(route: string): boolean {
  const normalized = normalizeAppRedirect(route);
  if (!normalized) return false;

  try {
    return window.sessionStorage.getItem(RESTORE_SCROLL_ROUTE_STORAGE_KEY) === normalized;
  } catch {
    return false;
  }
}

/** Rimuove il flag di restore scroll per evitare ripristini ripetuti. */
export function clearScrollRestoreMark(route: string): void {
  const normalized = normalizeAppRedirect(route);
  if (!normalized) return;

  try {
    if (window.sessionStorage.getItem(RESTORE_SCROLL_ROUTE_STORAGE_KEY) === normalized) {
      window.sessionStorage.removeItem(RESTORE_SCROLL_ROUTE_STORAGE_KEY);
    }
  } catch {
    /* noop */
  }
}